import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

export const SideDrawer = DialogPrimitive.Root;
export const SideDrawerTrigger = DialogPrimitive.Trigger;
export const SideDrawerClose = DialogPrimitive.Close;

/** `scrim` do master — $scrim (45% light / 60% dark), sem blur. */
const SideDrawerOverlay = forwardRef(function SideDrawerOverlay(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-ds-id="ds/overlay/side-drawer"
      className={cn(
        'fixed inset-0 z-50 bg-scrim/45 animate-fade-in dark:bg-scrim/60',
        className,
      )}
      {...props}
    />
  );
});

/**
 * `ds/overlay/side-drawer` — painel que abre da direita: superfície de card,
 * cantos $radius-ds-xl do lado esquerdo, borda $glass-border e sombra
 * $glass-shadow (-8px, blur 32). Estrutura: header · divider · content
 * (scroll) · divider · footer.
 */
export const SideDrawerContent = forwardRef(function SideDrawerContent(
  {
    className,
    contentClassName,
    children,
    title,
    description,
    header,
    footer,
    autoFocusOnOpen = false,
    onOpenAutoFocus,
    ...props
  },
  ref,
) {
  const handleOpenAutoFocus = (event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented) return;
    if (!autoFocusOnOpen) {
      event.preventDefault();
    }
  };

  const hasHeader = Boolean(header || title || description);

  return (
    <DialogPrimitive.Portal>
      <SideDrawerOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={handleOpenAutoFocus}
        data-ds-id="ds/overlay/side-drawer"
        className={cn(
          // eslint-disable-next-line no-restricted-syntax -- painel de 320px do .pen (min com 100%)
          'fixed inset-y-0 right-0 z-50 flex w-[min(100%,20rem)] flex-col overflow-hidden rounded-l-ds-xl border border-glass-border bg-card animate-drawer-in focus:outline-none',
          // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
          'shadow-[-8px_0_32px] shadow-glass-shadow',
          className,
        )}
        {...props}
      >
        {hasHeader ? (
          <>
            <header className="flex shrink-0 items-center justify-between gap-space-3 px-space-5 py-space-4 pt-[max(1rem,env(safe-area-inset-top))]">
              {header ?? (
                <>
                  <div className="min-w-0 flex flex-col gap-space-1">
                    {title ? (
                      <DialogPrimitive.Title className="font-display text-type-title-card font-semibold leading-tight text-foreground">
                        {title}
                      </DialogPrimitive.Title>
                    ) : null}
                    {description ? (
                      <DialogPrimitive.Description className="text-type-body-sm leading-snug text-muted-foreground">
                        {description}
                      </DialogPrimitive.Description>
                    ) : null}
                  </div>
                  <DialogPrimitive.Close
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-full bg-muted text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Fechar"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </DialogPrimitive.Close>
                </>
              )}
            </header>
            <div className="h-px shrink-0 bg-border" aria-hidden />
          </>
        ) : null}

        {/* `content` — o padding vive na instância (o .pen dá [$space-2,$space-5]
            no master e [$space-2,$space-3] na instância de navegação). */}
        <div
          className={cn(
            'scrollbar-hidden min-h-0 flex-1 overflow-y-auto overscroll-contain',
            contentClassName,
          )}
        >
          {children}
        </div>

        {footer ? (
          <>
            <div className="h-px shrink-0 bg-border" aria-hidden />
            <footer className="shrink-0 bg-card px-space-4 py-space-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {footer}
            </footer>
          </>
        ) : null}

        {!hasHeader ? (
          <DialogPrimitive.Close
            className="absolute right-space-4 top-[max(1rem,env(safe-area-inset-top))] flex h-8 w-8 items-center justify-center rounded-ds-full bg-muted text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" aria-hidden />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
